// src/components/admin/scheduling/eventsApi.js
// Firestore CRUD for the events collection

import { db } from "../../../firebaseConfig";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { logActivity } from "./loggingApi";
import { formatTimeFromMinutes } from "./timeUtils";

export const EVENTS_COL = "events";

const GOOGLE_CALENDAR_ID = "info@trueeducationcumming.com";

// ── Logging helpers ──────────────────────────────────────────────────────────

async function getAdminName() {
  const user = getAuth().currentUser;
  if (!user) return "Unknown Admin";
  return user.displayName || user.email || "Unknown Admin";
}

function formatEventTimeRange(startTime, endTime) {
  return `${formatTimeFromMinutes(startTime ?? 0)} – ${formatTimeFromMinutes(endTime ?? 0)}`;
}

// Produces: "Monday, 6/1/2026 (11:00 AM – 1:30 PM)"
function formatEventContext(dateKey, startTime, endTime) {
  if (!dateKey) return "Unknown Date";
  try {
    const [y, m, d] = dateKey.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const dateStr  = date.toLocaleDateString("en-US");
    return `${weekday}, ${dateStr} (${formatEventTimeRange(startTime, endTime)})`;
  } catch {
    return dateKey;
  }
}

// Compare only the fields that matter for user-visible changes
const CONTENT_FIELDS = ["title", "date", "startTime", "endTime", "notes", "recurrence"];
const POSITION_FIELDS = ["date", "startTime", "endTime"];

function getChangedFields(oldData, patch) {
  return CONTENT_FIELDS.filter((f) => {
    if (patch[f] === undefined) return false;
    if (f === "recurrence") return JSON.stringify(patch[f]) !== JSON.stringify(oldData[f]);
    return patch[f] !== oldData[f];
  });
}

// ── Utility ───────────────────────────────────────────────────────────────────

function toDateKeyStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

export async function createEvent(payload) {
  const auth = getAuth();
  const user = auth.currentUser;

  const docRef = await addDoc(collection(db, EVENTS_COL), {
    title: payload.title,
    date: payload.date,
    startTime: payload.startTime,
    endTime: payload.endTime,
    notes: payload.notes || null,
    recurrence: payload.recurrence || null,
    isRecurring: !!(payload.recurrence?.enabled),
    googleCalendarId: GOOGLE_CALENDAR_ID,
    googleEventId: null,
    syncStatus: "pending",
    syncError: null,
    lastSyncedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user?.uid || null,
    createdByName: user?.displayName || null,
  });

  // Log the creation
  try {
    const context = formatEventContext(payload.date, payload.startTime, payload.endTime);
    await logActivity("CREATE_EVENT", "Created event", {
      studentName: payload.title || "Untitled Event", // stored in studentName for search compat
      sessionContext: context,
      valFrom: "-",
      valTo: payload.title || "Untitled Event",
      adminName: await getAdminName(),
    });
  } catch (err) {
    console.warn("[eventsApi] Failed to log CREATE_EVENT:", err);
  }

  return docRef.id;
}

export async function updateEvent(eventId, patch) {
  const ref = doc(db, EVENTS_COL, eventId);

  // Read old data so we can compute a meaningful diff for logging
  let oldData = {};
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) oldData = snap.data();
  } catch (err) {
    console.warn("[eventsApi] Failed to read old event for diff:", err);
  }

  await updateDoc(ref, {
    ...patch,
    ...(patch.recurrence !== undefined ? { isRecurring: !!(patch.recurrence?.enabled) } : {}),
    syncStatus: "pending",
    syncError: null,
    updatedAt: serverTimestamp(),
  });

  // Log the change
  try {
    const changed = getChangedFields(oldData, patch);
    if (changed.length === 0) return; // no meaningful content change

    const positionChanged = POSITION_FIELDS.some((f) => changed.includes(f));
    const eventTitle = patch.title || oldData.title || "Untitled Event";

    if (positionChanged) {
      // Position (date/time) changed — log as MOVE_EVENT
      const oldDate      = oldData.date || "";
      const oldStart     = oldData.startTime ?? 0;
      const oldEnd       = oldData.endTime ?? 0;
      const newDate      = patch.date      ?? oldDate;
      const newStart     = patch.startTime ?? oldStart;
      const newEnd       = patch.endTime   ?? oldEnd;

      const oldContext   = formatEventContext(oldDate, oldStart, oldEnd);
      const valFrom      = formatEventContext(oldDate, oldStart, oldEnd);
      const valTo        = formatEventContext(newDate, newStart, newEnd);

      await logActivity("MOVE_EVENT", "Moved event", {
        studentName: eventTitle,
        sessionContext: oldContext,
        valFrom,
        valTo,
        adminName: await getAdminName(),
      });
    } else {
      // Content-only change — log as UPDATE_EVENT
      const fieldList = changed.join(", ");
      const context   = formatEventContext(
        patch.date      ?? oldData.date      ?? "",
        patch.startTime ?? oldData.startTime ?? 0,
        patch.endTime   ?? oldData.endTime   ?? 0,
      );
      await logActivity("UPDATE_EVENT", "Updated event", {
        studentName: eventTitle,
        sessionContext: context,
        valFrom: fieldList,
        valTo: fieldList,
        adminName: await getAdminName(),
      });
    }
  } catch (err) {
    console.warn("[eventsApi] Failed to log UPDATE_EVENT:", err);
  }
}

export async function deleteEvent(eventId) {
  const ref = doc(db, EVENTS_COL, eventId);

  // Read before deleting so we can log
  let oldData = {};
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) oldData = snap.data();
  } catch (err) {
    console.warn("[eventsApi] Failed to read event before delete:", err);
  }

  await deleteDoc(ref);

  // Log the deletion
  try {
    const context = formatEventContext(oldData.date, oldData.startTime, oldData.endTime);
    await logActivity("DELETE_EVENT", "Deleted event", {
      studentName: oldData.title || "Untitled Event",
      sessionContext: context,
      valFrom: "Active",
      valTo: "Deleted",
      adminName: await getAdminName(),
    });
  } catch (err) {
    console.warn("[eventsApi] Failed to log DELETE_EVENT:", err);
  }
}

// ── Real-time listener ────────────────────────────────────────────────────────

// Sets up a real-time onSnapshot listener for events visible in the given date range.
// Uses a 1-year lookback so that older recurring events still appear in the current view.
// Returns an unsubscribe function.
export function subscribeToEventsForRange(rangeStart, rangeEnd, onUpdate, onError) {
  const rangeEndKey = toDateKeyStr(rangeEnd);
  const lookback = new Date(rangeStart);
  lookback.setFullYear(lookback.getFullYear() - 1);
  const lookbackKey = toDateKeyStr(lookback);

  const q = query(
    collection(db, EVENTS_COL),
    where("date", ">=", lookbackKey),
    where("date", "<=", rangeEndKey)
  );

  return onSnapshot(q, (snap) => {
    const events = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    onUpdate(events);
  }, onError);
}
